import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ThemeSelector } from './ui/ThemeSelector';
import { DeliveryNotice, formatFrequency, getAllowance } from './ui/FieldNotes';
import { API_URL } from '../config';
import './Profile.css';
import { isDeletionGuard } from '../services/accountDeletion';

function accessEndText(profile) {
  const knownGrant = profile && (profile.entitlement_status === undefined || profile.entitlement_status === 'current')
    && typeof profile.plan_type === 'string' && profile.plan_type.trim()
    && getAllowance(profile) !== null && formatFrequency(profile.check_frequency) !== 'Not available';
  if (!knownGrant) return 'Not available';
  if (profile.end_date === null) return 'No expiry set';
  if (!profile.end_date) return 'Not available';
  const value = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(profile.end_date) ? `${profile.end_date.replace(' ', 'T')}Z` : profile.end_date;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleDateString();
}

export default function Profile() {
  const { token, submitDeletion, reportDeletion } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [retry, setRetry] = useState(0);
  const [emailPreference, setEmailPreference] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteButton = React.useRef(null);
  const restoreDeleteFocus = React.useRef(false);
  useEffect(() => {
    if (!showDeleteConfirm && restoreDeleteFocus.current) {
      deleteButton.current?.focus();
      restoreDeleteFocus.current = false;
    }
  }, [showDeleteConfirm]);
  // A token refresh must not overwrite a preference the user is editing.
  const loaded = React.useRef(false);
  useEffect(() => {
    if (!token || loaded.current) return;
    const controller = new AbortController();
    setLoading(true);
    fetch(`${API_URL}/profile`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal })
      .then(async response => { const data = await response.json(); if (controller.signal.aborted) return null; if (isDeletionGuard(response, data)) { reportDeletion(); return null; } if (!response.ok) throw new Error('We could not load your account. Please try again.'); return data; })
      .then(data => { if (data && !controller.signal.aborted) { setProfile(data); setEmailPreference(Boolean(data.email_notifications)); loaded.current = true; setError(''); } })
      .catch(err => { if (err.name !== 'AbortError') setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token, retry, reportDeletion]);

  async function handleNotificationUpdate(event) {
    event.preventDefault(); setSaving(true); setError(''); setSuccess('');
    try {
      const response = await fetch(`${API_URL}/profile/notifications`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email_notifications: emailPreference }),
      });
      const body = await response.json();
      if (isDeletionGuard(response, body)) { reportDeletion(); return; }
      if (!response.ok) throw new Error('Your preference could not be saved. Please try again.');
      setProfile(prev => ({ ...prev, email_notifications: emailPreference }));
      setSuccess('Email preference saved. Delivery also depends on the service being enabled.');
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  function handleDeleteAccount() {
    if (!showDeleteConfirm) { setShowDeleteConfirm(true); return; }
    submitDeletion();
  }

  if (loading) return <div className="loading" role="status">Loading your account…</div>;
  const allowance = getAllowance(profile);
  return <div className="profile-container">
    <header className="page-heading"><div><p className="eyebrow">YOUR BASE CAMP</p><h1>Your account</h1><p className="page-intro">A few preferences. A little peace of mind.</p></div></header>
    {error && <div className="alert alert-error" role="alert">{error}{!profile && <button className="btn btn-secondary" onClick={() => setRetry(value => value + 1)}>Try again</button>}</div>}
    {success && <div className="alert alert-success" role="status">{success}</div>}
    {profile && <div className="profile-grid">
      <section className="profile-card"><p className="eyebrow">THE ESSENTIALS</p><h2>Account details</h2><dl className="account-details">
        <div><dt>Email</dt><dd>{profile.email || 'Not available'}</dd></div>
        <div><dt>Member since</dt><dd>{profile.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Not available'}</dd></div>
      </dl></section>
      <section className="profile-card"><p className="eyebrow">YOUR ALLOWANCE</p><h2>Beta access</h2><dl className="account-details">
        <div><dt>Watch slots</dt><dd>{allowance ?? 'Not available'}</dd></div>
        <div><dt>Normal check interval</dt><dd>{formatFrequency(profile.check_frequency)}</dd></div>
        <div><dt>Access ends</dt><dd>{accessEndText(profile)}</dd></div>
      </dl><p className="setting-description">Each name on each platform uses a slot, including paused watches. Checks can be delayed by platform limits.</p><Link className="text-link" to="/upgrade">About the beta →</Link></section>
      <section className="profile-card notification-settings-card"><p className="eyebrow">STAY IN THE LOOP</p><h2>Email preference</h2><DeliveryNotice />
        <form onSubmit={handleNotificationUpdate}><label className="preference-check"><input type="checkbox" checked={emailPreference} onChange={event => { setEmailPreference(event.target.checked); setSuccess(''); }} disabled={saving} /><span>Email me about availability signals</span></label>
          <p className="setting-description">This saves your preference. Emails require delivery to be enabled and a supported availability signal. X checks cannot trigger availability emails.</p>
          <button className="btn btn-primary" disabled={saving || emailPreference === Boolean(profile.email_notifications)}>{saving ? 'Saving…' : 'Save preferences'}</button>
        </form>
      </section>
      <section className="profile-card"><p className="eyebrow">MAKE YOURSELF AT HOME</p><h2>Appearance</h2><p className="setting-description">Light, dark, or whatever your device prefers.</p><ThemeSelector variant="inline" /></section>
      <section className="profile-card danger-zone-card"><div><h2>Delete account</h2><p className="setting-description">Request permanent deletion of your sign-in account and NameCamper account records.</p>
        {showDeleteConfirm && <p id="delete-explanation" className="delete-confirmation">This cannot be undone. Your watches will be removed. A saved request continues on the server after you leave. A minimal deletion record is retained; backups and shared records are not all erased. A message already being sent may still arrive.</p>}</div>
        <div className="delete-actions">{showDeleteConfirm && <button className="btn btn-secondary" onClick={() => { restoreDeleteFocus.current = true; setShowDeleteConfirm(false); }}>Cancel</button>}
          <button ref={deleteButton} className="btn btn-danger" onClick={handleDeleteAccount} aria-describedby={showDeleteConfirm ? 'delete-explanation' : undefined}>{showDeleteConfirm ? 'Confirm Delete' : 'Delete Account'}</button></div>
      </section>
    </div>}
  </div>;
}
